import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { t } from '../i18n';
import { validateGuess, type GuessResult } from '../net/api';

const ROWS = 6;
const COLS = 5;

const COLORS = {
  cream: 0xf4efe6,
  creamDark: 0xe8e0d1,
  ink: 0x2a2620,
  inkSoft: 0x5a544a,
  accent: 0xc8754f,
  sage: 0x8aa18b,
  sand: 0xd9cdb5,
  shadow: 0x000000
};

type CellState = 'empty' | 'pending' | 'correct' | 'present' | 'absent';

interface Cell {
  container: Container;
  bg: Graphics;
  letter: Text;
  state: CellState;
}

export type LetterScoredCallback = (letter: string, state: 'correct' | 'present' | 'absent') => void;

export interface GameEndResult {
  win: boolean;
  answer: string | null;
  attempts: number; // number of rows used (1..6)
}
export type GameEndCallback = (result: GameEndResult) => void;

export class WordGame {
  private app: Application;
  private stage: Container;
  private grid: Container;
  private hintText!: Text;
  private cells: Cell[][] = [];
  private currentRow = 0;
  private currentCol = 0;
  private guesses: string[] = [];
  private gameOver = false;
  private busy = false;
  private letterScoredCb: LetterScoredCallback | null = null;
  private gameEndCb: GameEndCallback | null = null;

  private cellSize = 60;
  private gap = 8;

  constructor(app: Application) {
    this.app = app;
    this.stage = new Container();
    this.grid = new Container();
    this.app.stage.addChild(this.stage);
    this.stage.addChild(this.grid);
  }

  async init() {
    this.buildGrid();
    this.buildHint();
    this.layout();
    this.app.renderer.on('resize', () => this.layout());
  }

  onLetterScored(cb: LetterScoredCallback) { this.letterScoredCb = cb; }
  onGameEnd(cb: GameEndCallback) { this.gameEndCb = cb; }

  private buildGrid() {
    for (let r = 0; r < ROWS; r++) {
      const row: Cell[] = [];
      for (let c = 0; c < COLS; c++) {
        const cellCont = new Container();
        const bg = new Graphics();
        const letter = new Text({
          text: '',
          style: new TextStyle({
            fontFamily: 'Fraunces, serif',
            fontSize: 36,
            fontWeight: '300',
            fill: COLORS.ink,
            align: 'center'
          })
        });
        letter.anchor.set(0.5);
        cellCont.addChild(bg, letter);
        this.grid.addChild(cellCont);
        row.push({ container: cellCont, bg, letter, state: 'empty' });
      }
      this.cells.push(row);
    }
    this.paintAllCells();
  }

  private buildHint() {
    this.hintText = new Text({
      text: t('hint.start'),
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 12,
        fill: COLORS.inkSoft,
        letterSpacing: 1.5
      })
    });
    this.hintText.anchor.set(0.5, 0);
    this.stage.addChild(this.hintText);
  }

  private paintCell(cell: Cell) {
    const { bg, state } = cell;
    bg.clear();
    const w = this.cellSize;
    const r = 10;
    let fill = COLORS.cream;
    let stroke = COLORS.sand;
    let textColor = COLORS.ink;

    if (state === 'pending') {
      fill = COLORS.cream;
      stroke = COLORS.inkSoft;
    } else if (state === 'correct') {
      fill = COLORS.sage;
      stroke = COLORS.sage;
      textColor = 0xffffff;
    } else if (state === 'present') {
      fill = COLORS.accent;
      stroke = COLORS.accent;
      textColor = 0xffffff;
    } else if (state === 'absent') {
      fill = COLORS.creamDark;
      stroke = COLORS.creamDark;
      textColor = COLORS.inkSoft;
    }

    bg.roundRect(2, 3, w, w, r).fill({ color: COLORS.shadow, alpha: 0.06 });
    bg.roundRect(0, 0, w, w, r).fill(fill).stroke({ width: 1.5, color: stroke });
    cell.letter.style.fill = textColor;
  }

  private paintAllCells() {
    for (const row of this.cells) for (const cell of row) this.paintCell(cell);
  }

  private layout() {
    // app.screen is the canonical CSS-pixel size of the canvas in Pixi v8
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    // Compute optimal cell size — fit both width and height
    const maxByW = Math.min(screenW - 24, 420);
    const cellByW = Math.floor((maxByW - this.gap * (COLS - 1)) / COLS);
    const maxByH = Math.max(screenH - 80, 200); // leave room for hint
    const cellByH = Math.floor((maxByH - this.gap * (ROWS - 1)) / ROWS);
    this.cellSize = Math.max(36, Math.min(cellByW, cellByH, 68));

    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const cell = this.cells[r][c];
        cell.container.x = c * (this.cellSize + this.gap);
        cell.container.y = r * (this.cellSize + this.gap);
        cell.letter.x = this.cellSize / 2;
        cell.letter.y = this.cellSize / 2;
        cell.letter.style.fontSize = Math.floor(this.cellSize * 0.55);
        this.paintCell(cell);
      }
    }

    const gridW = COLS * this.cellSize + (COLS - 1) * this.gap;
    const gridH = ROWS * this.cellSize + (ROWS - 1) * this.gap;
    this.grid.x = Math.round((screenW - gridW) / 2);
    this.grid.y = Math.round(Math.max(12, (screenH - gridH - 32) / 2));

    this.hintText.x = Math.round(screenW / 2);
    this.hintText.y = Math.round(this.grid.y + gridH + 16);
  }

  // ---------- Public input API ----------
  input(ch: string) {
    if (this.gameOver || this.busy) return;
    if (this.currentCol >= COLS) return;
    const cell = this.cells[this.currentRow][this.currentCol];
    cell.letter.text = ch;
    cell.state = 'pending';
    this.paintCell(cell);
    cell.container.scale.set(0.9);
    this.tween(cell.container, 120);
    this.currentCol++;
  }

  backspace() {
    if (this.gameOver || this.busy) return;
    if (this.currentCol === 0) return;
    this.currentCol--;
    const cell = this.cells[this.currentRow][this.currentCol];
    cell.letter.text = '';
    cell.state = 'empty';
    this.paintCell(cell);
  }

  async submit() {
    if (this.gameOver || this.busy) return;
    if (this.currentCol < COLS) return;
    const guess = this.cells[this.currentRow].map((c) => c.letter.text).join('');
    this.busy = true;
    this.hintText.text = '…';
    try {
      const attemptNum = this.currentRow + 1;
      const result: GuessResult = await validateGuess(guess, attemptNum);
      if (!result.valid) {
        this.hintText.text = t('hint.notword');
        this.shakeRow(this.currentRow);
        this.busy = false;
        return;
      }
      for (let i = 0; i < COLS; i++) {
        const cell = this.cells[this.currentRow][i];
        cell.state = result.marks![i];
        this.paintCell(cell);
        if (this.letterScoredCb) this.letterScoredCb(guess[i], result.marks![i]);
      }
      this.guesses.push(guess);
      if (result.win) {
        this.hintText.text = t('hint.win');
        this.gameOver = true;
        this.gameEndCb?.({ win: true, answer: result.answer ?? guess, attempts: attemptNum });
      } else if (this.currentRow >= ROWS - 1) {
        this.hintText.text = t('hint.loss') + (result.answer ? ` — ${result.answer}` : '');
        this.gameOver = true;
        this.gameEndCb?.({ win: false, answer: result.answer ?? null, attempts: attemptNum });
      } else {
        this.hintText.text = t('hint.start');
        this.currentRow++;
        this.currentCol = 0;
      }
    } catch (e) {
      this.hintText.text = 'network error';
      console.error(e);
    } finally {
      this.busy = false;
    }
  }

  private shakeRow(_r: number) {
    let tt = 0;
    const baseX = this.grid.x;
    const ticker = (delta: any) => {
      tt += (delta?.deltaTime ?? 1);
      this.grid.x = baseX + Math.sin(tt * 1.2) * 6;
      if (tt > 20) {
        this.grid.x = baseX;
        this.app.ticker.remove(ticker);
      }
    };
    this.app.ticker.add(ticker);
  }

  private tween(target: Container, duration: number) {
    const start = performance.now();
    const startScale = target.scale.x;
    const end = 1;
    const step = (now: number) => {
      const k = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - k, 3);
      const s = startScale + (end - startScale) * eased;
      target.scale.set(s);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}
